import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Play, Pause, RotateCcw, Volume2, AlertCircle, Waves, Lock, FastForward } from "lucide-react";
import { Slider } from "../components/ui/slider";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { getLibraryPractice, renderInlineItalic } from "../lib/libraryPractices";
import { useLibraryAudio } from "../contexts/LibraryAudioContext";
import { useBilling } from "../contexts/BillingContext";
import { isLockedForUser, isTrialExpired } from "../lib/trialAccess";
import { getProgress, formatProgressTime, clearProgress } from "../lib/practiceProgress";
import { AppMenu } from "../components/AppMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Library player view. The actual <audio> element + Web Audio graph live in
// LibraryAudioContext (mounted at App root) so audio survives route changes
// and continues playing while the app is backgrounded. This page is just a UI
// layer that reads / writes the context.

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

const LibraryPlayerPage = () => {
  const { practiceId } = useParams();
  const navigate = useNavigate();
  const practice = getLibraryPractice(practiceId);

  // Billing status via the shared BillingContext (one fetch at
  // sign-in). Same lock logic the Dashboard uses; enforced here in case
  // the user deep-links to /library/<id> without visiting Dashboard first.
  const { billing } = useBilling();
  const billingStatus = billing?.status || null;

  const locked = practice ? isLockedForUser(practice.id, billingStatus) : false;

  const {
    practice: activePractice,
    isPlaying,
    currentTime,
    duration,
    volume,
    bilateralOn,
    error,
    setBilateralOn,
    load,
    play,
    toggle,
    seekTo,
    restart,
    setVolume,
  } = useLibraryAudio();

  // "Resume where you left off". Saved client-side by LibraryAudioContext
  // every ~5s during playback. We read it once when the user lands on the
  // page so the chip stays stable while they listen. It hides as soon as
  // they either tap Resume (we seek + play) or the playhead drifts past
  // the saved point (e.g. they tapped the main Play button from 0:00).
  const [resumePoint, setResumePoint] = useState(null);
  const [resumeDismissed, setResumeDismissed] = useState(false);
  useEffect(() => {
    setResumePoint(practice ? getProgress(practice.id) : null);
    setResumeDismissed(false);
  }, [practice]);

  // Load this practice into the global audio context the first time the page
  // mounts (or when navigating between two different library entries).
  // Trial-locked practices are never loaded — we render a paywall instead.
  useEffect(() => {
    if (practice && !locked) load(practice);
  }, [practice, load, locked]);

  if (!practice) {
    return (
      <div className="min-h-screen bg-[#FBF8F1] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h1 className="font-outfit font-semibold text-2xl text-[#1B2349] mb-3">
            Practice not found
          </h1>
          <button
            onClick={() => navigate("/library")}
            className="px-6 py-3 rounded-full bg-[#1B2349] text-white font-medium"
            data-testid="missing-practice-back-btn"
          >
            Back to library
          </button>
        </div>
      </div>
    );
  }

  // Trial paywall — shown when the user is trialing AND this practice
  // isn't on the TRIAL_ACCESS_IDS whitelist. Keeps the look of the
  // library player but replaces playback controls with an upgrade CTA.
  if (locked) {
    const expired = isTrialExpired(billingStatus);
    return (
      <div className="min-h-screen bg-[#FBF8F1]" data-testid={`library-player-locked-${practice.id}`}>
        <header className="glass-header border-b border-slate-100 sticky top-0 z-50">
          <div className="px-6 sm:px-10 py-5 max-w-5xl mx-auto flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-slate-100 transition-colors"
              data-testid="library-player-back-btn"
              aria-label="Back"
            >
              <ArrowLeft className="w-5 h-5 text-[#1B2349]" />
            </button>
            <AppMenu />
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-6 pt-8 pb-20 text-center">
          <div
            className="aspect-square w-44 mx-auto rounded-3xl bg-cover bg-center mb-7 ring-1 ring-white/40 shadow-[0_10px_40px_rgba(27,35,73,0.18)]"
            style={{
              backgroundImage: `url('${practice.image}')`,
              backgroundPosition: practice.imagePosition || "center",
            }}
            aria-hidden
          />
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E2B887]/15 text-[#7A5C2E] text-xs font-medium mb-4">
            <Lock className="w-3.5 h-3.5" />
            {expired ? "Trial ended — subscribe to continue" : "Unlock with a paid plan"}
          </div>
          <h1 className="font-outfit font-semibold text-3xl sm:text-4xl text-[#1B2349] mb-3 leading-tight">
            {practice.title}
          </h1>
          <p className="text-[#4A5568] text-base sm:text-lg leading-relaxed mb-8 max-w-lg mx-auto">
            {practice.description}
          </p>
          <p className="text-sm text-[#718096] mb-6 max-w-md mx-auto">
            {expired
              ? "Your free trial has ended. Subscribe to keep using every guided meditation and the full Bilateral Calm library."
              : <>During your free trial you can listen to <strong>Mindfulness of Breath</strong>. Upgrade any time to unlock every guided meditation and the full Bilateral Calm library.</>}
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              onClick={() => navigate("/pricing")}
              className="btn-primary text-base px-7 py-3"
              data-testid="library-player-upgrade-btn"
            >
              {expired ? "Subscribe now" : "See plans & upgrade"}
            </Button>
            {!expired && (
              <Button
                variant="outline"
                onClick={() => navigate("/library/mindfulness-of-breath")}
                className="rounded-full border-slate-200"
                data-testid="library-player-try-free-btn"
              >
                Try Mindfulness of Breath
              </Button>
            )}
          </div>
        </main>
      </div>
    );
  }

  // Until the practice is loaded into the context (next tick after mount),
  // gate state values to safe defaults so the UI doesn't show stale numbers
  // from a previously-loaded track.
  const isThisLoaded = activePractice?.id === practice.id;
  const time = isThisLoaded ? currentTime : 0;
  const dur = isThisLoaded ? duration : 0;
  const playing = isThisLoaded ? isPlaying : false;
  const progressPercent = dur > 0 ? (time / dur) * 100 : 0;

  // Show the resume chip only when (a) we actually have a stored point,
  // (b) the audio for THIS practice is loaded and has a duration we can
  // seek into, and (c) the user hasn't already passed the saved point.
  // Dismiss the moment the playhead crosses it (covers both Resume taps
  // and the user tapping the main Play button to start from 0:00).
  const showResume =
    Boolean(resumePoint) &&
    !resumeDismissed &&
    isThisLoaded &&
    dur > 0 &&
    time < (resumePoint?.position || 0) - 1;
  const onResume = async () => {
    if (!resumePoint) return;
    setResumeDismissed(true);
    seekTo(resumePoint.position);
    await play();
  };
  const onStartOver = () => {
    setResumeDismissed(true);
    if (practice) clearProgress(practice.id);
    seekTo(0);
  };

  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid={`library-player-${practice.id}`}>
      <header className="glass-header border-b border-slate-100 sticky top-0 z-50">
        <div className="px-6 sm:px-10 py-5 max-w-5xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="text-[#4A5568] hover:text-[#1B2349] flex items-center gap-2 px-3 py-2 rounded-full hover:bg-white/60"
            data-testid="player-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <AppMenu />
        </div>
      </header>

      <section className="px-6 sm:px-10 pb-32 max-w-3xl mx-auto">
        <div
          className="h-64 sm:h-80 rounded-3xl bg-cover bg-center mb-8"
          style={{ backgroundImage: `url('${practice.image}')` }}
        />

        <p className="text-xs uppercase tracking-[0.2em] text-[#3090A8] font-medium mb-3">
          {practice.duration} · {practice.category}
        </p>
        <h1 className="font-outfit font-semibold text-3xl sm:text-4xl text-[#1B2349] tracking-tight mb-4 leading-tight">
          {practice.title}
        </h1>
        <p className="text-lg text-[#4A5568] leading-relaxed mb-10">
          {renderInlineItalic(practice.description).map((s) =>
            s.italic ? <em key={s.key}>{s.value}</em> : <span key={s.key}>{s.value}</span>
          )}
        </p>

        {error && (
          <div
            className="bg-orange-50 border border-orange-200 rounded-2xl p-4 mb-6 flex gap-3 items-start"
            data-testid="player-error-banner"
          >
            <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-orange-900 leading-relaxed">{error}</p>
          </div>
        )}

        {/* Player card */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)] border border-slate-100">
          {/* Time + progress */}
          <div className="flex justify-between text-sm text-[#718096] mb-2">
            <span data-testid="player-current-time">{formatTime(time)}</span>
            <span data-testid="player-duration">{formatTime(dur)}</span>
          </div>
          <Slider
            value={[progressPercent]}
            onValueChange={(v) => {
              if (dur > 0) seekTo((v[0] / 100) * dur);
            }}
            max={100}
            step={0.1}
            className="mb-6"
            data-testid="player-progress-slider"
          />

          {/* Resume chip — appears when this practice has a saved point
              from a previous session. Tapping "Resume" seeks + plays;
              "Start over" wipes the saved point and snaps to 0:00. */}
          {showResume && (
            <div
              className="mb-6 rounded-2xl bg-[#3090A8]/10 border border-[#3090A8]/25 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
              data-testid="player-resume-chip"
            >
              <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
                <div className="w-9 h-9 rounded-full bg-[#3090A8]/15 flex items-center justify-center shrink-0">
                  <FastForward className="w-4 h-4 text-[#1B2349]" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#1B2349] leading-tight">
                    Pick up where you left off
                  </p>
                  <p className="text-xs text-[#4A5568] mt-0.5">
                    Last listened at {formatProgressTime(resumePoint.position)}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  onClick={onResume}
                  className="rounded-full bg-[#1B2349] text-white hover:bg-[#1A202C] text-sm h-9 px-4"
                  data-testid="player-resume-btn"
                >
                  Resume
                </Button>
                <Button
                  variant="outline"
                  onClick={onStartOver}
                  className="rounded-full border-slate-200 text-sm h-9 px-4"
                  data-testid="player-start-over-btn"
                >
                  Start over
                </Button>
              </div>
            </div>
          )}

          {/* Transport controls */}
          <div className="flex items-center justify-center gap-5 mb-6">
            <button
              onClick={restart}
              className="w-12 h-12 rounded-full flex items-center justify-center bg-slate-100 hover:bg-slate-200 transition-colors"
              data-testid="player-restart-btn"
            >
              <RotateCcw className="w-5 h-5 text-[#1B2349]" />
            </button>
            <button
              onClick={toggle}
              className="w-16 h-16 rounded-full flex items-center justify-center bg-[#1B2349] text-white hover:bg-[#1A202C] transition-colors active:scale-95"
              data-testid="player-play-btn"
            >
              {playing ? (
                <Pause className="w-7 h-7" />
              ) : (
                <Play className="w-7 h-7 ml-0.5" />
              )}
            </button>
            <div className="w-12" /> {/* visual balance */}
          </div>

          {/* Volume */}
          <div className="flex items-center gap-3 max-w-xs mx-auto">
            <Volume2 className="w-4 h-4 text-[#718096]" />
            <Slider
              value={[volume * 100]}
              onValueChange={(v) => setVolume(v[0] / 100)}
              max={100}
              step={1}
              className="flex-1"
              data-testid="player-volume-slider"
            />
          </div>

          {/* Bilateral pan toggle — only when the practice opts in */}
          {practice.bilateralCapable && (
            <div
              className="mt-6 pt-5 border-t border-slate-100 flex items-center gap-3"
              data-testid="bilateral-toggle-row"
            >
              <Waves className="w-4 h-4 text-[#3090A8] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1B2349] leading-tight">
                  Bilateral movement
                </p>
                <p className="text-xs text-[#718096] mt-0.5">
                  Sound pans left ↔ right at {practice.bilateralBpm || 50} bpm
                </p>
              </div>
              <Switch
                checked={bilateralOn}
                onCheckedChange={setBilateralOn}
                data-testid="bilateral-toggle"
              />
            </div>
          )}
        </div>

        <p className="text-sm text-[#718096] mt-6 text-center">
          Audio keeps playing if you leave this screen or minimise the app.
          Headphones recommended for full bilateral effect.
        </p>
      </section>
    </div>
  );
};

export default LibraryPlayerPage;
